import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { updateAccountAddresses } from '../../store/actions/account/accountActions';
import useGtmHook from '../../util/hooks/useGtmHook';
import { ADDRESS_SET_TO_DEFAULT } from '../../constants/gtmEvents';
import { formatAddressStringInline } from '../../util/helpers/addressHelper';
import { mediaBelow, pxToRem, pxToRemMd } from '../../assets/styles/helper';
import themeColors from '../../assets/styles/themeColors';
import { variables } from '../../assets/styles/variables';
import AddressStatus from './AddressStatus';
import { selectIsLoadingByActionType } from '../../store/selectors/loadingSelectors';
import { ACCOUNT_ADDRESS_UPDATE_LOADING } from '../../store/actions/account/accountActionConstants';
import addressTagType, {
  ADDRESS_TAG_TYPES,
  ADDRESS_TAG_TYPES_ICONS,
} from '../../util/enum/api/addressTagType';
import useGetCountries from '../../util/hooks/useGetCountries';
import Paragraph from '../Paragraph/Paragraph';
import { ReactComponent as Trash } from '../../assets/images/svg/trash.svg';

export const AddressCardButtons = styled.div`
  position: absolute;
  bottom: ${pxToRem(5)};
  right: ${pxToRem(10)};
  display: flex;

  > :first-child {
    font-size: ${pxToRemMd(12)};
    margin-right: ${pxToRem(8)};
  }
`;

export const AddressCardDetails = styled.div`
  display: flex;
  align-items: center;
  font-size: ${pxToRem(14)};
  line-height: 1.75;
  text-align: left;
  color: ${themeColors.colorTextPrimary};
  font-weight: 500;

  span {
    font-size: ${pxToRem(12)};
    font-weight: 400;
    color: ${themeColors.colorTextSecondary};
    display: block;
  }
`;

export const AddressCardDefault = styled.p`
  position: absolute;
  top: 0;
  right: 0;
  background-color: ${themeColors.colorTextSecondary};
  color: ${themeColors.colorWhite};
  display: inline-flex;
  padding: 2px 6px;
  justify-content: center;
  align-items: center;
  font-size: ${pxToRem(14)};
  font-weight: 500;
  border-radius: 0 3px 0 2px;
`;

export const AddressCardName = styled.p`
  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-weight: 500;
  }
`;

export const AddressCardAddress = styled.p`
  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-weight: 500;
  }
`;

export const AddressesCard = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid transparent;
  border-radius: ${variables.borderRadius.borderRadius};
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};
  background-color: ${themeColors.colorBackgroundSecondary};
  position: relative;
  padding: 10px;
  ${(props) =>
    props.isAddressCardDefault && `border-color: ${themeColors.colorFocus}`};
`;

const DeliveryAddressIcon = styled.div`
  width: ${pxToRem(32)};
  height: ${pxToRem(32)};
  background-color: ${themeColors.colorGrey};
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 10px;
  border-radius: ${variables.borderRadius.borderRadius};

  ${mediaBelow(variables.breakpoints.bpMd)} {
    width: ${pxToRemMd(28)};
    height: ${pxToRemMd(28)};
  }
`;

const DefaultButton = styled.button`
  background: none;
  border: none;
  text-decoration: underline;
  font-size: ${pxToRem(12)};
  cursor: pointer;
  min-width: 0;
  &:hover {
    text-decoration: none;
  }
`;

const DeleteButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  min-width: 0;
`;

const AddressCard = ({
  address,
  hasChangePermission,
  accountUid,
  setAddressUid,
  setIsDeleteModalOpen,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { gtmEvent } = useGtmHook();
  const isLoading = useSelector(
    selectIsLoadingByActionType([ACCOUNT_ADDRESS_UPDATE_LOADING]),
  );
  const countries = useGetCountries();

  if (!address) {
    return null;
  }

  const { AddressUid, IsDefaultMailingAddress } = address;

  const makeDefault = () => {
    const handleGtm = () => {
      gtmEvent(ADDRESS_SET_TO_DEFAULT);
    };

    dispatch(
      updateAccountAddresses({
        accountUid,
        addressUid: AddressUid,
        data: [
          {
            op: 'replace',
            path: '/IsDefaultMailingAddress',
            value: true,
          },
        ],
        handleGtm,
      }),
    );
  };

  return (
    <AddressesCard isAddressCardDefault={IsDefaultMailingAddress}>
      <AddressCardDetails>
        {address?.TagType && (
          <DeliveryAddressIcon>
            {ADDRESS_TAG_TYPES_ICONS[address?.TagType]}
          </DeliveryAddressIcon>
        )}
        <div>
          {address?.TagType && (
            <Paragraph fontSize={14}>
              {address?.TagType === ADDRESS_TAG_TYPES.OTHER
                ? address?.TagNote
                : addressTagType[address?.TagType]}
            </Paragraph>
          )}
          <span>{formatAddressStringInline(address, countries)}</span>
        </div>
      </AddressCardDetails>
      <AddressStatus isVerified={address.IsVerified} />

      {IsDefaultMailingAddress ? (
        <AddressCardDefault>{t('common.default')}</AddressCardDefault>
      ) : (
        <AddressCardButtons>
          <DefaultButton
            disabled={!hasChangePermission || isLoading}
            onClick={makeDefault}
          >
            {t('common.setAsDefault')}
          </DefaultButton>
          <DeleteButton
            disabled={!hasChangePermission}
            onClick={() => {
              setAddressUid(AddressUid);
              setIsDeleteModalOpen(true);
            }}
          >
            <Trash />
          </DeleteButton>
        </AddressCardButtons>
      )}
    </AddressesCard>
  );
};

AddressCard.propTypes = {
  address: PropTypes.shape({
    AddressUid: PropTypes.string,
    RecipientCaption: PropTypes.string,
    IsDefaultMailingAddress: PropTypes.bool,
    IsVerified: PropTypes.bool,
    BrokerAccountHolderType: PropTypes.number,
    TagType: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    TagNote: PropTypes.string,
  }),
  hasChangePermission: PropTypes.bool,
  accountUid: PropTypes.string,
  setAddressUid: PropTypes.func,
  setIsDeleteModalOpen: PropTypes.func,
};

export default AddressCard;
